import type { CaseCptLine, Laterality, ScheduledCase, SurgeonTemplate } from '@/lib/templates/types';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import { clinicalSeverityEvidenceText, kneeMuaNarrativeVariables } from './evidenceValidation';

export const STRICT_JSON_TO_TEXT_SYSTEM_BOUNDARY =
  "CRITICAL: You are a strict JSON-to-Text template compiler. DO NOT include meta-commentary, instructions, or notes about what the system 'should' do. Output ONLY the clinical narrative text.";

const STUB_NARRATIVE_PATTERN = /operative narrative will be generated|please use the ai-generate|no dictation provided/i;

function cptCode(line: Pick<CaseCptLine, 'code'>): string {
  return String(line.code);
}

function cptModifiers(line: Pick<CaseCptLine, 'modifiers'>): string[] {
  return Array.isArray(line.modifiers) ? line.modifiers : [];
}

function lateralityModifier(laterality: Laterality | null): string {
  if (laterality === 'right') return 'RT';
  if (laterality === 'left') return 'LT';
  if (laterality === 'bilateral') return '50';
  return '';
}

function operativeSide(laterality: Laterality | null): string {
  if (laterality === 'right') return 'right';
  if (laterality === 'left') return 'left';
  return 'operative';
}

function sanitizeOperativeNoteText(note: string): string {
  return note
    .replace(/,\s*and\s+chondroplasty\s+treated\s+as\s+bundled\s+work/gi, '; no separate chondroplasty CPT is requested')
    .replace(/Chondroplasty\s+is\s+treated\s+as\s+bundled\s+work\s+in\s+this\s+demo[^.]*\.\s*/gi, '')
    .replace(/This\s+prevents\s+unnecessary\s+NCCI\/bundling\s+exposure\s+before\s+the\s+packet\s+is\s+generated\.\s*/gi, '')
    .replace(/\s+([.;,])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

function formatCptLine(line: CaseCptLine, laterality: Laterality | null): string {
  const modifiers = cptModifiers(line).length > 0
    ? cptModifiers(line)
    : line.section === 'primary' && lateralityModifier(laterality)
      ? [lateralityModifier(laterality)]
      : [];
  const suffix = modifiers.length > 0 ? `-${modifiers.join('-')}` : '';
  return `${line.description || `CPT ${line.code}`} (CPT ${line.code}${suffix})`;
}

function caseDate(selectedCase: ScheduledCase): string {
  return selectedCase.scheduled_date?.slice(0, 10) || 'not provided';
}

function diagnosisText(template: SurgeonTemplate): string {
  const diagnoses = (template.diagnosis_codes || [])
    .map(dx => `${dx.description || dx.code}${dx.code ? ` (${dx.code})` : ''}`)
    .filter(Boolean);
  return diagnoses.join('; ') || template.template_name;
}

function proceduresText(cptLines: CaseCptLine[], laterality: Laterality | null): string {
  return cptLines.map(line => formatCptLine(line, laterality)).join('; ');
}

export function shouldInjectShoulderCompartmentTransition(
  cptLines: Array<Pick<CaseCptLine, 'code' | 'modifiers'>>,
): boolean {
  const shoulderCompartmentCodes = new Set(['29826', '29827', '29828']);
  return cptLines.some(line => cptCode(line) === '29826')
    || cptLines.some(line =>
      shoulderCompartmentCodes.has(cptCode(line)) &&
      cptModifiers(line).some(modifier => modifier.toUpperCase() === 'XS'),
    );
}

export function shouldInjectKneeMuaManipulation(
  cptLines: Array<Pick<CaseCptLine, 'code'>>,
): boolean {
  return cptLines.some(line => cptCode(line) === '27570');
}

export function shouldInjectCarpalTunnelReleaseTechnique(
  cptLines: Array<Pick<CaseCptLine, 'code'>>,
): boolean {
  return cptLines.some(line => cptCode(line) === '64721');
}

export function shouldInjectExtensorMechanismRepair(
  cptLines: Array<Pick<CaseCptLine, 'code'>>,
): boolean {
  return cptLines.some(line => cptCode(line) === '27385');
}

export function shouldInjectKneeArthroscopyMeniscectomy(
  cptLines: Array<Pick<CaseCptLine, 'code'>>,
): boolean {
  return cptLines.some(line => ['29880', '29881'].includes(cptCode(line)));
}

export function shouldInjectTotalKneeArthroplasty(
  cptLines: Array<Pick<CaseCptLine, 'code'>>,
): boolean {
  return cptLines.some(line => ['27447', '27435'].includes(cptCode(line)));
}

export function shouldInjectOpenShoulderDistinctStructureWork(
  cptLines: Array<Pick<CaseCptLine, 'code'>>,
): boolean {
  const codes = cptLines.map(cptCode);
  return codes.includes('23420') && (codes.includes('23120') || codes.includes('29824'));
}

export function buildShoulderCompartmentTransitionBlock(): string {
  return [
    'Operative Findings & Compartment Transition:',
    'Diagnostic arthroscopy of the glenohumeral joint was performed. The primary rotator cuff tear [CPT 29827] and biceps pathology [CPT 29828] were identified and addressed within the glenohumeral joint space.',
    'Attention was then turned to a distinct anatomical structure: The arthroscope was redirected out of the glenohumeral joint and into the subacromial space. Extensive subacromial bursitis and a Type III hooked acromion were identified. A thorough subacromial decompression and acromioplasty [CPT 29826] was performed in this separate compartment.',
  ].join('\n');
}

function normalizeFlexionForBlock(value: string): string {
  return value
    .replace(/\s*(?:degrees?|°)\b/gi, '')
    .replace(/\s*-\s*/g, ' to ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildKneeMuaManipulationBlock(kneeRomFlexion: string): string {
  return buildKneeMuaManipulationBlockWithPostRom(kneeRomFlexion, '120 degrees of flexion and full extension');
}

function buildKneeMuaManipulationBlockWithPostRom(kneeRomFlexion: string, postManipulationRom: string): string {
  const flexion = normalizeFlexionForBlock(kneeRomFlexion) || '[knee_rom_flexion]';
  return [
    'Operative Findings & Manipulation:',
    `The patient was placed under general anesthesia to achieve complete muscle relaxation. Pre-manipulation range of motion was evaluated and found to be rigidly restricted at ${flexion} degrees of flexion. Gentle, sustained flexion pressure was applied to the joint. Audible and palpable lysis of fibrotic adhesions was noted within the joint space. Post-manipulation range of motion significantly improved to ${postManipulationRom}. There was no evidence of fracture, ligamentous compromise, or patellar tendon avulsion during the manipulation.`,
  ].join('\n');
}

export function buildCarpalTunnelReleaseTechniqueBlock(laterality: Laterality | null = null): string {
  const side = operativeSide(laterality);
  return [
    'Operative Findings & Median Nerve Decompression:',
    `A longitudinal incision was made in the palm of the ${side} wrist along the ulnar border of the thenar crease. Dissection was carried down through the subcutaneous tissue and palmar fascia. The transverse carpal ligament was identified and divided sharply under direct visualization from distal to proximal, completely decompressing the median nerve within the carpal tunnel. The median nerve was inspected after release and confirmed to be freely decompressed without residual constriction. This operative technique supports median neuroplasty with release of the transverse carpal ligament [CPT 64721].`,
  ].join('\n');
}

export function buildExtensorMechanismRepairBlock(laterality: Laterality | null = null): string {
  const side = operativeSide(laterality);
  return [
    'Operative Findings & Extensor Mechanism Repair:',
    `The ${side} quadriceps tendon and VMO disruption were identified at the extensor mechanism. The tendon edges were mobilized, hematoma and nonviable frayed tissue were debrided only as needed for repair, and primary repair of the quadriceps tendon/VMO extensor mechanism was performed [CPT 27385]. Repair integrity was confirmed through controlled knee motion without gapping, restoring continuity of the extensor mechanism.`,
  ].join('\n');
}

export function buildKneeArthroscopyMeniscectomyBlock(laterality: Laterality | null = null): string {
  const side = operativeSide(laterality);
  return [
    'Operative Findings & Meniscectomy:',
    `Diagnostic arthroscopy of the ${side} knee was performed through standard portals. The medial compartment demonstrated a torn medial meniscus, and partial medial meniscectomy was performed with basket forceps and shaver to a stable rim. The lateral compartment demonstrated a torn lateral meniscus, and partial lateral meniscectomy was performed to a stable rim. The performed arthroscopic work supports ${side} knee arthroscopy with partial medial and lateral meniscectomy [CPT 29880]; no separate chondroplasty CPT is requested.`,
  ].join('\n');
}

export function buildTotalKneeArthroplastyBlock(laterality: Laterality | null = null): string {
  const side = operativeSide(laterality);
  return [
    'Operative Findings & Total Knee Arthroplasty:',
    `The ${side} knee demonstrated end-stage tricompartmental degenerative disease with fixed flexion contracture. Total knee arthroplasty [CPT 27447] was performed with bone preparation, trialing, final component implantation, and confirmation of tracking and stability. Posterior capsular release and collateral ligament balancing [CPT 27435] were performed as distinct contracture-balancing work to restore extension and gap balance.`,
  ].join('\n');
}

export function buildOpenShoulderDistinctStructureBlock(laterality: Laterality | null = null): string {
  const side = operativeSide(laterality);
  return [
    'Operative Findings & Open Shoulder Distinct-Structure Work:',
    `Open rotator cuff repair with acromioplasty of the ${side} shoulder [CPT 23420] addressed the chronic cuff tear and outlet impingement. Attention was then directed to a separate anatomic structure, the acromioclavicular joint, where distal clavicle resection/acromioclavicular joint excision [CPT 23120] was performed to address the separate AC joint pain generator.`,
  ].join('\n');
}

function postManipulationRomFromText(text: string): string {
  const range = text.match(/\b(?:restoration|restored|improved)[^.\n]{0,100}?(?:motion|range\s+of\s+motion)?[^.\n]{0,60}?(\d{1,3})\s*(?:to|-)\s*(\d{1,3})\s*degrees?/i)
    || text.match(/\bmotion[^.\n]{0,80}?(?:restored|improved|from)[^.\n]{0,60}?(\d{1,3})\s*(?:to|-)\s*(\d{1,3})\s*degrees?/i);
  if (!range) return '120 degrees of flexion and full extension';
  return `${range[1]} to ${range[2]} degrees`;
}

export function ensureShoulderCompartmentTransition(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
): string {
  const candidate = (noteText || '').trim();
  const base = candidate && !STUB_NARRATIVE_PATTERN.test(candidate) ? candidate : fallbackNote.trim();
  if (!shouldInjectShoulderCompartmentTransition(cptLines)) return base;
  if (/Operative Findings & Compartment Transition/i.test(base)) return base;
  return sanitizeOperativeNoteText([base, buildShoulderCompartmentTransitionBlock()].filter(Boolean).join('\n\n'));
}

export function ensureKneeMuaManipulationBlock(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
  clinicalText = '',
  clinicalSeverityData?: ClinicalSeverityData | null,
): string {
  const candidate = (noteText || '').trim();
  const base = candidate && !STUB_NARRATIVE_PATTERN.test(candidate) ? candidate : fallbackNote.trim();
  if (!shouldInjectKneeMuaManipulation(cptLines)) return base;
  const knee = kneeMuaNarrativeVariables(clinicalText || base, clinicalSeverityData);
  if (/Operative Findings & Manipulation/i.test(base)) {
    const flexion = normalizeFlexionForBlock(knee.romFlexion);
    return sanitizeOperativeNoteText(base
      .replace(/\$\{knee_rom_flexion\}|\[knee_rom_flexion\]/g, flexion)
      .replace(/\s+degrees\s+degrees\b/gi, ' degrees'));
  }
  return sanitizeOperativeNoteText([base, buildKneeMuaManipulationBlockWithPostRom(knee.romFlexion, postManipulationRomFromText(`${clinicalText}\n${base}`))].filter(Boolean).join('\n\n'));
}

export function ensureCarpalTunnelReleaseTechnique(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
  laterality: Laterality | null = null,
): string {
  const candidate = (noteText || '').trim();
  const base = candidate && !STUB_NARRATIVE_PATTERN.test(candidate) ? candidate : fallbackNote.trim();
  if (!shouldInjectCarpalTunnelReleaseTechnique(cptLines)) return base;
  const alreadyDocumentsTechnique = /transverse carpal ligament/i.test(base)
    && /direct visualization/i.test(base)
    && /median nerve/i.test(base)
    && /\b(decompress|decompressed|decompression|release|released)\b/i.test(base);
  if (/Operative Findings & Median Nerve Decompression/i.test(base) || alreadyDocumentsTechnique) return base;
  return sanitizeOperativeNoteText([base, buildCarpalTunnelReleaseTechniqueBlock(laterality)].filter(Boolean).join('\n\n'));
}

export function ensureExtensorMechanismRepairBlock(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
  laterality: Laterality | null = null,
): string {
  const candidate = (noteText || '').trim();
  const base = candidate && !STUB_NARRATIVE_PATTERN.test(candidate) ? candidate : fallbackNote.trim();
  if (!shouldInjectExtensorMechanismRepair(cptLines)) return base;
  if (/Operative Findings & Extensor Mechanism Repair/i.test(base)) return sanitizeOperativeNoteText(base);
  return sanitizeOperativeNoteText([base, buildExtensorMechanismRepairBlock(laterality)].filter(Boolean).join('\n\n'));
}

export function ensureKneeArthroscopyMeniscectomyBlock(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
  laterality: Laterality | null = null,
): string {
  const candidate = (noteText || '').trim();
  const base = candidate && !STUB_NARRATIVE_PATTERN.test(candidate) ? candidate : fallbackNote.trim();
  if (!shouldInjectKneeArthroscopyMeniscectomy(cptLines)) return base;
  if (/Operative Findings & Meniscectomy/i.test(base)) return sanitizeOperativeNoteText(base);
  return sanitizeOperativeNoteText([base, buildKneeArthroscopyMeniscectomyBlock(laterality)].filter(Boolean).join('\n\n'));
}

export function ensureTotalKneeArthroplastyBlock(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
  laterality: Laterality | null = null,
): string {
  const candidate = (noteText || '').trim();
  const base = candidate && !STUB_NARRATIVE_PATTERN.test(candidate) ? candidate : fallbackNote.trim();
  if (!shouldInjectTotalKneeArthroplasty(cptLines)) return base;
  if (/Operative Findings & Total Knee Arthroplasty/i.test(base)) return sanitizeOperativeNoteText(base);
  return sanitizeOperativeNoteText([base, buildTotalKneeArthroplastyBlock(laterality)].filter(Boolean).join('\n\n'));
}

export function ensureOpenShoulderDistinctStructureBlock(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
  laterality: Laterality | null = null,
): string {
  const candidate = (noteText || '').trim();
  const base = candidate && !STUB_NARRATIVE_PATTERN.test(candidate) ? candidate : fallbackNote.trim();
  if (!shouldInjectOpenShoulderDistinctStructureWork(cptLines)) return base;
  if (/Operative Findings & Open Shoulder Distinct-Structure Work/i.test(base)) return sanitizeOperativeNoteText(base);
  return sanitizeOperativeNoteText([base, buildOpenShoulderDistinctStructureBlock(laterality)].filter(Boolean).join('\n\n'));
}

export function ensureOperativeTemplateBlocks(
  noteText: string | null | undefined,
  cptLines: CaseCptLine[],
  fallbackNote = '',
  clinicalText = '',
  clinicalSeverityData?: ClinicalSeverityData | null,
  laterality: Laterality | null = null,
): string {
  const withKnee = ensureKneeMuaManipulationBlock(noteText, cptLines, fallbackNote, clinicalText, clinicalSeverityData);
  const withCarpal = ensureCarpalTunnelReleaseTechnique(withKnee, cptLines, fallbackNote, laterality);
  const withExtensor = ensureExtensorMechanismRepairBlock(withCarpal, cptLines, fallbackNote, laterality);
  const withMeniscectomy = ensureKneeArthroscopyMeniscectomyBlock(withExtensor, cptLines, fallbackNote, laterality);
  const withTka = ensureTotalKneeArthroplastyBlock(withMeniscectomy, cptLines, fallbackNote, laterality);
  const withOpenShoulder = ensureOpenShoulderDistinctStructureBlock(withTka, cptLines, fallbackNote, laterality);
  return sanitizeOperativeNoteText(ensureShoulderCompartmentTransition(withOpenShoulder, cptLines, fallbackNote));
}

function packetText(
  selectedCase: ScheduledCase,
  dictation?: string | null,
  clinicalSeverityData?: ClinicalSeverityData | null,
): string {
  const docText = (selectedCase.pre_loaded_docs || [])
    .map(doc => doc.extracted_text)
    .filter(Boolean)
    .join('\n\n');
  const clinicalMetrics = (selectedCase as { clinical_metrics?: unknown }).clinical_metrics;
  return [
    selectedCase.scheduled_procedure,
    docText,
    dictation,
    clinicalSeverityEvidenceText(clinicalSeverityData),
    clinicalMetrics ? JSON.stringify(clinicalMetrics) : null,
  ].filter(Boolean).join('\n\n');
}

function clinicalSeverityFromCase(
  selectedCase: ScheduledCase,
  clinicalSeverityData?: ClinicalSeverityData | null,
): ClinicalSeverityData | null {
  return clinicalSeverityData || ((selectedCase as { clinical_metrics?: ClinicalSeverityData }).clinical_metrics || null);
}

function buildDeterministicKneeMuaOperativeNote(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  cptLines: CaseCptLine[];
  laterality: Laterality | null;
  positioning: string;
  ebl: string;
  operativeTime: string;
  dictation?: string | null;
  highComplexityParagraph?: string;
  varianceText?: string;
  clinicalSeverityData?: ClinicalSeverityData | null;
}): string {
  const {
    selectedCase,
    template,
    cptLines,
    laterality,
    positioning,
    ebl,
    operativeTime,
    dictation,
    highComplexityParagraph,
    varianceText,
  } = params;
  const structured = clinicalSeverityFromCase(selectedCase, params.clinicalSeverityData);
  const text = packetText(selectedCase, dictation, structured);
  const knee = kneeMuaNarrativeVariables(text, structured);
  const cleanDictation = (dictation || '').trim();

  if (cleanDictation && !STUB_NARRATIVE_PATTERN.test(cleanDictation)) {
    return ensureKneeMuaManipulationBlock(
      [
        cleanDictation,
        highComplexityParagraph?.trim(),
        varianceText ? `Variance: ${varianceText}` : '',
      ].filter(Boolean).join('\n\n'),
      cptLines,
      '',
      text,
      structured,
    );
  }

  return [
    `DATE OF SERVICE: ${caseDate(selectedCase)}`,
    `PATIENT: ${selectedCase.patient_name}`,
    `MRN: ${selectedCase.patient_mrn || 'not provided'}`,
    '',
    `Pre-operative Diagnosis: ${diagnosisText(template)}`,
    `Post-operative Diagnosis: ${diagnosisText(template)}`,
    `Procedures Performed: ${proceduresText(cptLines, laterality)}`,
    'Surgeon: John J. Larkin, MD',
    'Anesthesia: General anesthesia.',
    `Positioning: ${positioning || 'Supine'}`,
    '',
    'Findings:',
    buildKneeMuaManipulationBlock(knee.romFlexion),
    '',
    'Procedure Description:',
    'The patient was identified in the pre-operative area, the operative site was marked, and informed consent was confirmed. The patient was transported to the operating room and placed under general anesthesia to permit complete muscle relaxation. Pre-manipulation range of motion was evaluated, and controlled sustained manipulation was performed as documented above. The knee was reassessed after manipulation and confirmed to have improved motion without evidence of fracture, ligamentous compromise, or extensor mechanism injury.',
    '',
    'Implants: None.',
    `Estimated Blood Loss: ${ebl}`,
    `Operative Time: ${operativeTime}`,
    'Complications: None documented.',
    'Disposition: The patient was awakened and transferred to recovery in stable condition.',
    highComplexityParagraph?.trim() || '',
    varianceText ? `Variance: ${varianceText}` : '',
  ].filter(Boolean).join('\n');
}

function buildDeterministicCarpalTunnelReleaseOperativeNote(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  cptLines: CaseCptLine[];
  laterality: Laterality | null;
  positioning: string;
  ebl: string;
  operativeTime: string;
  highComplexityParagraph?: string;
  varianceText?: string;
}): string {
  const {
    selectedCase,
    template,
    cptLines,
    laterality,
    positioning,
    ebl,
    operativeTime,
    highComplexityParagraph,
    varianceText,
  } = params;

  return [
    `DATE OF SERVICE: ${caseDate(selectedCase)}`,
    `PATIENT: ${selectedCase.patient_name}`,
    `MRN: ${selectedCase.patient_mrn || 'not provided'}`,
    '',
    `Pre-operative Diagnosis: ${diagnosisText(template)}`,
    `Post-operative Diagnosis: ${diagnosisText(template)}`,
    `Procedures Performed: ${proceduresText(cptLines, laterality)}`,
    'Surgeon: John J. Larkin, MD',
    'Anesthesia: Monitored anesthesia care/local anesthesia as documented by anesthesia.',
    `Positioning: ${positioning || 'Supine with hand table'}`,
    '',
    'Findings:',
    buildCarpalTunnelReleaseTechniqueBlock(laterality),
    '',
    'Procedure Description:',
    'The patient was identified in the pre-operative area, the operative site was marked, and informed consent was confirmed. The patient was transported to the operating room, positioned with the operative hand accessible on the hand table, prepped, and draped in sterile fashion. Surgical release and median nerve decompression were performed as documented above. Hemostasis was obtained, the wound was irrigated, and closure was performed in standard fashion.',
    '',
    'Implants: None.',
    `Estimated Blood Loss: ${ebl}`,
    `Operative Time: ${operativeTime}`,
    'Complications: None documented.',
    'Disposition: The patient was awakened and transferred to recovery in stable condition.',
    highComplexityParagraph?.trim() || '',
    varianceText ? `Variance: ${varianceText}` : '',
  ].filter(Boolean).join('\n');
}

export function buildDeterministicShoulderOperativeNote(params: {
  selectedCase: ScheduledCase;
  template: SurgeonTemplate;
  cptLines: CaseCptLine[];
  laterality: Laterality | null;
  positioning: string;
  ebl: string;
  operativeTime: string;
  dictation?: string | null;
  highComplexityParagraph?: string;
  varianceText?: string;
  implants?: Array<{ description: string; qty?: number; catalog_id?: string }>;
  clinicalSeverityData?: ClinicalSeverityData | null;
}): string {
  const {
    selectedCase,
    template,
    cptLines,
    laterality,
    positioning,
    ebl,
    operativeTime,
    dictation,
    highComplexityParagraph,
    varianceText,
    implants,
  } = params;

  if (shouldInjectKneeMuaManipulation(cptLines)) {
    return buildDeterministicKneeMuaOperativeNote(params);
  }

  const cleanDictation = (dictation || '').trim();
  if (cleanDictation && !STUB_NARRATIVE_PATTERN.test(cleanDictation)) {
    const structured = clinicalSeverityFromCase(selectedCase, params.clinicalSeverityData);
    return ensureOperativeTemplateBlocks(
      [
        cleanDictation,
        highComplexityParagraph?.trim(),
        varianceText ? `Variance: ${varianceText}` : '',
      ].filter(Boolean).join('\n\n'),
      cptLines,
      '',
      packetText(selectedCase, dictation, structured),
      structured,
      laterality,
    );
  }

  if (shouldInjectCarpalTunnelReleaseTechnique(cptLines)) {
    return buildDeterministicCarpalTunnelReleaseOperativeNote(params);
  }

  const primaryLine = cptLines.find(line => line.section === 'primary') || cptLines[0];
  const primaryProcedure = primaryLine?.description || template.template_name;
  const implantText = implants?.length
    ? implants.map(implant => `${implant.qty || 1}x ${implant.description}${implant.catalog_id ? ` (${implant.catalog_id})` : ''}`).join('; ')
    : 'None documented in the template.';
  const complicationText = /no complications/i.test(`${selectedCase.pre_loaded_docs?.map(doc => doc.extracted_text).join(' ') || ''} ${selectedCase.scheduled_procedure || ''}`)
    ? 'No complications documented.'
    : 'No intraoperative complications documented in the supplied case packet.';

  return [
    `DATE OF SERVICE: ${caseDate(selectedCase)}`,
    `PATIENT: ${selectedCase.patient_name}`,
    `MRN: ${selectedCase.patient_mrn || 'not provided'}`,
    '',
    `Pre-operative Diagnosis: ${diagnosisText(template)}`,
    `Post-operative Diagnosis: ${diagnosisText(template)}`,
    `Procedures Performed: ${proceduresText(cptLines, laterality)}`,
    'Surgeon: John J. Larkin, MD',
    'Anesthesia: General anesthesia with regional block as documented by anesthesia.',
    `Positioning: ${positioning}`,
    '',
    'Findings:',
    `Right shoulder arthroscopy confirmed pathology corresponding to ${primaryProcedure}.`,
    buildShoulderCompartmentTransitionBlock(),
    '',
    'Procedure Description:',
    'The patient was identified in the pre-operative area, the operative site was marked, and informed consent was confirmed. The patient was transported to the operating room, placed under anesthesia, positioned, prepped, and draped in sterile fashion. Arthroscopic portals were established and diagnostic arthroscopy was performed. The rotator cuff tear was repaired arthroscopically. The biceps pathology was treated with arthroscopic biceps tenodesis when included in the authorized CPT bundle. The arthroscope was then redirected to the separate subacromial compartment for decompression and acromioplasty as documented above.',
    '',
    `Implants: ${implantText}`,
    `Estimated Blood Loss: ${ebl}`,
    `Operative Time: ${operativeTime}`,
    `Complications: ${complicationText}`,
    'Disposition: The patient was awakened and transferred to recovery in stable condition.',
    highComplexityParagraph?.trim() || '',
    varianceText ? `Variance: ${varianceText}` : '',
  ].filter(Boolean).join('\n');
}
